import { ProgressBar } from '@core/components/ProgressBar';
import { Button } from '@soteria/design-system-ve';
import { useState } from 'react';

import * as styles from './LabelWithScoreThresholdSelector.css';

// Utility function to convert score threshold to human-readable label
const getScoreLabel = (score: number): string => {
  if (score <= 50) {
    return 'Permissive';
  } else if (score <= 70) {
    return 'Moderate';
  } else {
    return 'Strict';
  }
};

// Utility function to get description for score ranges
const getScoreDescription = (score: number): string => {
  if (score <= 50) {
    return 'High-confidence violations only (0-50)';
  } else if (score <= 70) {
    return 'Balanced approach (51-70)';
  } else {
    return 'Catches most potential violations (71-100)';
  }
};

export interface LabelWithScore {
  labelId: string;
  scoreThreshold: number;
}

export interface ScoreLabelOption {
  id: string;
  labelName: string;
}

interface LabelWithScoreThresholdSelectorProps {
  availableLabels: ScoreLabelOption[];
  selectedLabels: LabelWithScore[];
  loading?: boolean;
  title?: string;
  emptyStateMessage?: string;
  minScore?: number;
  maxScore?: number;
  defaultScoreThreshold?: number;
  showScoreDescription?: boolean;
  onChange: (labels: LabelWithScore[]) => void;
}

// eslint-disable-next-line complexity
export function LabelWithScoreThresholdSelector({
  availableLabels,
  selectedLabels,
  loading,
  title = 'Image Labels',
  emptyStateMessage = 'No image labels selected. Add labels to define score threshold criteria.',
  minScore = 0,
  maxScore = 100,
  defaultScoreThreshold = 70,
  showScoreDescription = true,
  onChange,
}: LabelWithScoreThresholdSelectorProps) {
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedLabelId, setSelectedLabelId] = useState('');
  const [scoreThreshold, setScoreThreshold] = useState(defaultScoreThreshold);

  const unselectedLabels = availableLabels.filter(label => !selectedLabels.some(selected => selected.labelId === label.id));

  const handleAddLabel = () => {
    if (selectedLabelId && selectedLabelId.trim() && scoreThreshold >= minScore && scoreThreshold <= maxScore) {
      const newLabel: LabelWithScore = {
        labelId: selectedLabelId.trim(),
        scoreThreshold,
      };

      onChange([...selectedLabels, newLabel]);
      setSelectedLabelId('');
      setScoreThreshold(defaultScoreThreshold);
      setShowAddForm(false);
    }
  };

  const handleRemoveLabel = (labelId: string) => {
    onChange(selectedLabels.filter(label => label.labelId !== labelId));
  };

  const handleScoreChange = (labelId: string, newScore: number) => {
    onChange(selectedLabels.map(label => (label.labelId === labelId ? { ...label, scoreThreshold: newScore } : label)));
  };

  const getLabelName = (labelId: string) => availableLabels.find(label => label.id === labelId)?.labelName || labelId;

  const getScoreColor = (scoreValue: number): 'error' | 'warning' | 'success' => {
    if (scoreValue <= 50) return 'error'; // Permissive
    if (scoreValue <= 70) return 'warning'; // Moderate

    return 'success'; // Strict
  };

  if (loading) {
    return <div className={styles.loading}>Loading labels...</div>;
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.title}>{title}</div>
        {unselectedLabels.length > 0 && <Button onClick={() => setShowAddForm(!showAddForm)}>{showAddForm ? 'Cancel' : 'Add Image Label'}</Button>}
      </div>

      {/* Add Label Form */}
      {showAddForm && (
        <div className={styles.addForm}>
          <div className={styles.formField}>
            <label className={styles.fieldLabel} htmlFor="labelSelect">
              Select Image Label
            </label>
            <select className={styles.select} id="labelSelect" value={selectedLabelId} onChange={e => setSelectedLabelId(e.target.value)}>
              <option value="">Choose an image label...</option>
              {unselectedLabels.map(label => (
                <option key={label.id} value={label.id}>
                  {label.labelName}
                </option>
              ))}
            </select>
          </div>

          <div className={styles.formField}>
            <label className={styles.fieldLabel} htmlFor="scoreSlider">
              Score Threshold: {getScoreLabel(scoreThreshold)} ({scoreThreshold})
            </label>
            {showScoreDescription && (
              <div className={styles.scoreDescription} id="score-description">
                {getScoreDescription(scoreThreshold)}
              </div>
            )}
            <div className={styles.sliderContainer}>
              <span aria-hidden="true" className={styles.sliderLabel}>
                {minScore}
              </span>
              <div className={styles.sliderWrapper}>
                <input
                  aria-describedby={showScoreDescription ? 'score-description' : undefined}
                  className={styles.slider}
                  id="scoreSlider"
                  max={maxScore}
                  min={minScore}
                  type="range"
                  value={scoreThreshold}
                  onChange={e => setScoreThreshold(parseInt(e.target.value, 10))}
                />
                <div
                  aria-label={`Score threshold: ${getScoreLabel(scoreThreshold)} (${scoreThreshold} out of ${maxScore})`}
                  className={styles.progressBarWrapper}
                  role="img"
                >
                  <ProgressBar color={getScoreColor(scoreThreshold)} size="small" value={(scoreThreshold / maxScore) * 100} />
                </div>
              </div>
              <span aria-hidden="true" className={styles.sliderLabel}>
                {maxScore}
              </span>
            </div>
          </div>

          <div className={styles.formActions}>
            <Button disabled={!selectedLabelId || !selectedLabelId.trim()} onClick={handleAddLabel}>
              Add Image Label
            </Button>
            <Button onClick={() => setShowAddForm(false)}>Cancel</Button>
          </div>
        </div>
      )}

      {/* Selected Labels List */}
      {selectedLabels.length > 0 && (
        <div>
          <div className={styles.sectionTitle}>Selected Image Labels ({selectedLabels.length})</div>
          <div className={styles.labelsList}>
            {selectedLabels.map(label => (
              <div key={label.labelId} className={styles.labelItem}>
                <div className={styles.labelInfo}>
                  <div className={styles.labelName}>{getLabelName(label.labelId)}</div>
                  <div className={styles.labelScore}>
                    Score threshold: {getScoreLabel(label.scoreThreshold)} ({label.scoreThreshold})
                  </div>
                  {showScoreDescription && <div className={styles.labelScoreDesc}>{getScoreDescription(label.scoreThreshold)}</div>}
                </div>
                <div className={styles.labelControls}>
                  <div className={styles.scoreControl}>
                    <input
                      aria-describedby={showScoreDescription ? `score-desc-${label.labelId}` : undefined}
                      aria-label={`Score threshold for ${getLabelName(label.labelId)}: ${getScoreLabel(label.scoreThreshold)} (${label.scoreThreshold} out of ${maxScore})`}
                      className={styles.controlSlider}
                      max={maxScore}
                      min={minScore}
                      type="range"
                      value={label.scoreThreshold}
                      onChange={e => handleScoreChange(label.labelId, parseInt(e.target.value, 10))}
                    />
                    <div
                      aria-label={`Score level: ${getScoreLabel(label.scoreThreshold)} (${label.scoreThreshold} out of ${maxScore})`}
                      className={styles.controlProgressBar}
                      role="img"
                    >
                      <ProgressBar color={getScoreColor(label.scoreThreshold)} size="compact" value={(label.scoreThreshold / maxScore) * 100} />
                    </div>
                    <span aria-hidden="true" className={styles.scoreValue}>
                      {label.scoreThreshold}
                    </span>
                  </div>
                  <Button aria-label={`Remove ${getLabelName(label.labelId)} label`} onClick={() => handleRemoveLabel(label.labelId)}>
                    Remove
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {selectedLabels.length === 0 && (
        <div aria-live="polite" className={styles.emptyState} role="status">
          {emptyStateMessage}
        </div>
      )}
    </div>
  );
}
